import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, TextField, Typography } from "@mui/material";
import TableMaestros from "../../../verticales/TableMaestrosInformes.tsx";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import MDButton from "components/MDButton";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import MDBox from "components/MDBox";
import fetchData from "verticales/FetchData";
// import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AppLoading from "verticales/AppLoading.tsx";
import MDBadge from "components/MDBadge";

const getEstadoBadge = (cpuValue, onClick) => {
  let color = "sucess";
  let label = "Bajo";

  if (cpuValue >= 80) {
    color = "error";
    label = "Alto";
  } else if (cpuValue >= 40) {
    color = "warning";
    label = "OK";
  }

  return (
    <MDBadge
      badgeContent={label}
      color={color}
      variant="gradient"
      size="sm"
      onClick={() => onClick(label)}
      sx={{
        cursor: "pointer",
        minWidth: 70,
        justifyContent: "center",
        display: "flex",
        textAlign: "center",
        paddingX: "6px",
      }}
    />
  );
};

const ViewConsumoEc2 = ({ data = [], loading = false }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [fechaIni, setFechaIni] = useState(dayjs(new Date()));
  const [fechaFin, setFechaFin] = useState(dayjs(new Date()));
  const [blocking, setBlocking] = useState(false);
  const [estadoFilter, setEstadoFilter] = useState(null);

  const url = "https://5djo5qwa3l.execute-api.us-east-1.amazonaws.com/dimo-goyito/ec2/";
  const context = "informes";

  const handleButtonClick = async () => {
    if (fechaIni && fechaFin) {
      try {
        setBlocking(true);

        const fechaIniTimestamp = fechaIni.startOf("hour").valueOf();
        const fechaFinTimestamp = fechaFin.startOf("hour").valueOf();

        const requestPayload = {
          action: "ec2-metricas-order-cpu-desc",
          object: {
            dateIni: fechaIniTimestamp,
            dateFin: fechaFinTimestamp,
          },
        };

        const response = await fetchData(url, context, requestPayload);

        if (response && response.rows) {
          setFilteredData(response.rows);
        } else {
          setFilteredData([]);
        }
      } catch (error) {
        console.error("Error al obtener datos:", error);
        setFilteredData([]);
      } finally {
        setBlocking(false);
      }
    }
  };

  const handleExportData = () => {
    const rowsToExport = filteredData;

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rowsToExport[0] || {}).join(","),
        ...rowsToExport.map((row) =>
          Object.values(row)
            .map((val) => `${val}`)
            .join(",")
        ),
      ].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "datos_exportados.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const dataToDisplay = useMemo(() => {
    if (!estadoFilter) return filteredData;

    return filteredData.filter((item) => {
      const estado =
        item.promedio_cpu_maximo >= 75 ? "Alto" : item.promedio_cpu_maximo >= 50 ? "OK" : "Bajo";
      return estado === estadoFilter;
    });
  }, [filteredData, estadoFilter]);

  const columns = useMemo(
    () => [
      { accessorKey: "instance_id", header: "Instance ID", size: 200 },
      { accessorKey: "promedio_cpu_maximo", header: "Promedio CPU Max", size: 180 },
      { accessorKey: "promedio_ram_maximo", header: "Promedio RAM Max", size: 180 },
      { accessorKey: "total_metricas_analizdas", header: "Total Métricas", size: 160 },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 120,
        Cell: ({ row }) =>
          getEstadoBadge(row.original.promedio_cpu_maximo, (estado) => {
            setEstadoFilter((prev) => (prev === estado ? null : estado));
          }),
      },
      { accessorKey: "instance_name", header: "Nombre Instancia", size: 300 },
      { accessorKey: "cuenta", header: "Cuenta", size: 150 },
      { accessorKey: "producto", header: "Producto", size: 180 },
      { accessorKey: "cliente", header: "Cliente", size: 200 },
      { accessorKey: "instance_type", header: "Tipo de Instancia", size: 150 },
    ],
    []
  );

  const theme = createTheme({
    components: {
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&.MuiMultiSectionDigitalClockSection-item": {
              fontSize: "1rem",
            },
          },
        },
      },
    },
  });

  return (
    <Box sx={{ marginTop: "1px", position: "relative" }}>
      <MDBox m={1}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box components={[]}>
            <ThemeProvider theme={theme}>
              <DateTimePicker
                label="inicial timestamp"
                onChange={(newValue) => setFechaIni(newValue)}
                value={fechaIni}
                inputFormat="dd/MM/yyyy hh:mm a"
                ampm={true}
                renderInput={(params) => <TextField {...params} fullWidth />}
                sx={{
                  "& .MuiInputBase-input": {
                    padding: "12px",
                    fontSize: 13,
                  },
                }}
              />
              <DateTimePicker
                label="final timestamp"
                onChange={(newValue) => setFechaFin(newValue)}
                value={fechaFin}
                inputFormat="dd/MM/yyyy hh:mm a"
                ampm={true}
                renderInput={(params) => <TextField {...params} fullWidth />}
                sx={{
                  "& .MuiInputBase-input": {
                    padding: "12px",
                    fontSize: 13,
                  },
                }}
              />
            </ThemeProvider>
            <MDButton
              variant="contained"
              disableElevation
              onClick={handleButtonClick}
              disabled={blocking}
            >
              {blocking ? "Buscando..." : "Buscar"}
            </MDButton>
          </Box>
        </LocalizationProvider>
      </MDBox>

      {/* Tabla siempre visible */}
      <Box sx={{ position: "relative" }}>
        <TableMaestros
          height={320}
          arrayColumns={columns}
          data={dataToDisplay}
          enableGrouping
          enableStickyHeader
          enableColumnResizing
          muiTableContainerProps={{
            sx: { maxHeight: "calc(100vh - 400px)", minHeight: "150px" },
          }}
          muiTableBodyCellProps={{
            sx: {
              borderRight: "1px solid #e0e0e0",
              fontSize: 14,
              padding: "0.5rem",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: theme.palette.mode === "light" ? "#f5f5f5" : "#303030",
              fontSize: "14px",
              fontWeight: "bold",
            },
          }}
          renderTopToolbarCustomActions={() => (
            <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap", padding: "4px" }}>
              <MDButton
                onClick={handleExportData}
                startIcon={<FileDownloadIcon />}
                sx={{ fontSize: "12px", fontWeight: "normal" }}
              >
                Exportar Datos
              </MDButton>

              {estadoFilter && (
                <MDButton
                  onClick={() => setEstadoFilter(null)}
                  color="info"
                  variant="outlined"
                  sx={{ fontSize: "12px", fontWeight: "normal" }}
                >
                  Limpiar filtro de estado
                </MDButton>
              )}
            </Box>
          )}
          initialState={{
            density: "compact",
            pagination: { pageIndex: 0, pageSize: 50 },
          }}
        />

        {/* Loader flotante cuando blocking es true */}
        {blocking && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AppLoading titleCustom="Métricas EC2" />
          </Box>
        )}
      </Box>
    </Box>
  );
};

ViewConsumoEc2.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      cuenta: PropTypes.string,
      instance_id: PropTypes.string.isRequired,
      instance_name: PropTypes.string,
      producto: PropTypes.string,
      cliente: PropTypes.string,
      instance_type: PropTypes.string,
      promedio_cpu_maximo: PropTypes.number,
      promedio_ram_maximo: PropTypes.number,
      total_metricas_analizadas: PropTypes.number,
    })
  ),
  loading: PropTypes.bool,
};

export default ViewConsumoEc2;
